import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LearningDashboard = ({ courses }) => {
  const completedCourses = courses?.filter(c => c?.completed)?.length || 0;
  const totalCourses = courses?.length || 0;
  const inProgressCourses = courses?.filter(c => c?.progress > 0 && !c?.completed)?.length || 0;
  const certifications = courses?.filter(c => c?.completed && c?.certification)?.length || 0;
  const progressPercentage = totalCourses > 0 ? Math.round((completedCourses / totalCourses) * 100) : 0;

  const recommendedCourses = courses?.filter(c => !c?.completed && c?.progress === 0)?.slice(0, 3) || [];

  return (
    <div className="space-y-6">
      {/* Progress Overview Card */}
      <div className="bg-gradient-to-br from-green-700 via-emerald-600 to-green-700 text-white rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-heading font-bold">Your Progress</h2>
          <div className="bg-white/20 backdrop-blur-sm p-2 rounded-full">
            <Icon name="TrendingUp" size={24} color="white" />
          </div>
        </div>

        {/* Progress Circle */}
        <div className="flex items-center justify-center mb-6">
          <div className="relative w-32 h-32">
            <svg className="w-full h-full transform -rotate-90">
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="rgba(255,255,255,0.2)"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="white"
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 56}`}
                strokeDashoffset={`${2 * Math.PI * 56 * (1 - progressPercentage / 100)}`}
                strokeLinecap="round"
                className="transition-all duration-500"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-3xl font-bold">{progressPercentage}%</span>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="CheckCircle" size={16} color="white" />
              <span className="text-xs opacity-90">Completed</span>
            </div>
            <p className="text-2xl font-bold">{completedCourses}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Clock" size={16} color="white" />
              <span className="text-xs opacity-90">In Progress</span>
            </div>
            <p className="text-2xl font-bold">{inProgressCourses}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Award" size={16} color="white" />
              <span className="text-xs opacity-90">Certifications</span>
            </div>
            <p className="text-2xl font-bold">{certifications}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="BookOpen" size={16} color="white" />
              <span className="text-xs opacity-90">Total Courses</span>
            </div>
            <p className="text-2xl font-bold">{totalCourses}</p>
          </div>
        </div>
      </div>
      {/* Certification Status */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Award" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-heading font-semibold text-foreground">Certification Status</h3>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-success/10 border border-success/20 rounded-lg">
            <div className="flex items-center gap-2">
              <Icon name="CheckCircle" size={18} color="var(--color-success)" />
              <span className="text-sm font-medium text-foreground">Basic User</span>
            </div>
            <span className="text-xs font-semibold text-success">Certified</span>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted/50 border border-border rounded-lg">
            <div className="flex items-center gap-2">
              <Icon name="Lock" size={18} color="var(--color-muted-foreground)" />
              <span className="text-sm font-medium text-muted-foreground">Advanced User</span>
            </div>
            <span className="text-xs font-semibold text-muted-foreground">Locked</span>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted/50 border border-border rounded-lg">
            <div className="flex items-center gap-2">
              <Icon name="Lock" size={18} color="var(--color-muted-foreground)" />
              <span className="text-sm font-medium text-muted-foreground">Expert Trainer</span>
            </div>
            <span className="text-xs font-semibold text-muted-foreground">Locked</span>
          </div>
        </div>
      </div>
      {/* Recommended Courses */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Sparkles" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-heading font-semibold text-foreground">Recommended for You</h3>
        </div>

        <div className="space-y-3">
          {recommendedCourses?.length > 0 ? (
            recommendedCourses?.map((course) => (
              <div key={course?.id} className="p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
                <h4 className="text-sm font-semibold text-foreground mb-1 line-clamp-2">
                  {course?.title}
                </h4>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon name="Clock" size={12} color="currentColor" />
                  <span>{course?.duration}</span>
                  <span>•</span>
                  <span>{course?.modules} modules</span>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No recommendations available
            </p>
          )}
        </div>
      </div>
      {/* Quick Actions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Quick Actions</h3>
        <div className="space-y-2">
          <Button fullWidth variant="outline" iconName="Download">
            Download Certificate
          </Button>
          <Button fullWidth variant="outline" iconName="FileText">
            View Transcript
          </Button>
          <Button fullWidth variant="outline" iconName="Share2">
            Share Progress
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LearningDashboard;